import moment from 'moment';
import { fetchCasualGenreProfileMetrics } from '@intelligence/v3/services/casualGenreProfile';
import { isEmpty } from '@intelligence/v3/utils/common';
import { generateMetricKey, generateSortMetricKey } from '@intelligence/v3/utils/metric';
import { formatFilterFetchParams, getMetricFiltersOfMetricValueLimit } from '../utils';
import { buildTopGamesMetricFilter } from '../utils/jumperUrlBuilder';
import { SortMode, SortType } from '@home/enums/common';
import { MAX_ROWS_LIMIT } from '@intelligence/components/DownloadCommon/DownloadPage/utils';
import { getDatasourceMetrics, getGameMetric, getGameDataSourceMetric } from '../constants';

import type {
  IGenreTableModuleUpdateParams,
  TDataSourceType,
  TMetricType,
} from '@intelligence/v3/pages/CasualGenreProfile/types';

const getMetrics = (granularity: IGranularity, dataSource: TDataSourceType, metric: TMetricType): string[] => {
  const granularityMetrics = getDatasourceMetrics(granularity);
  const dataSourceMetric = generateMetricKey({ dataSource, metric });

  if (!dataSourceMetric) {
    return granularityMetrics;
  }

  return [dataSourceMetric];
};

export const fetchCasualGenreProfileMainGenreMetrics = async (
  dataSource: TDataSourceType,
  metric: TMetricType,
  filterParams: any,
) => {
  const metricOrderBy = generateSortMetricKey({ dataSource, metric }, SortType.Desc);
  const filterFetchParams = formatFilterFetchParams(filterParams);
  const params = {
    ...filterFetchParams,
    metrics: getMetrics(filterFetchParams?.granularity, dataSource, metric),
    group_by: ['sensortower_genre'],
    order_by: [metricOrderBy],
    page: 1,
    page_size: MAX_ROWS_LIMIT,
    genre_keywords: [],
  };
  return fetchCasualGenreProfileMetrics('main_genre', params);
};

export const fetchCasualGenreProfileMainGenreMetricsSeparateByDate = (
  dataSource: TDataSourceType,
  metric: TMetricType,
  filterParams: any,
) => {
  const metricOrderBy = generateSortMetricKey({ dataSource, metric }, SortType.Desc);
  const filterFetchParams = formatFilterFetchParams(filterParams);
  const params = {
    ...filterFetchParams,
    metrics: getMetrics(filterFetchParams?.granularity, dataSource, metric),
    group_by: ['date', 'sensortower_genre'],
    order_by: [metricOrderBy],
    page: 1,
    page_size: MAX_ROWS_LIMIT,
    genre_keywords: [],

    top: {
      group_by: ['sensortower_genre'],
      order_by: [metricOrderBy],
      size: 10,
    },
  };
  return fetchCasualGenreProfileMetrics('main_genre_by_date', params);
};

export const fetchCasualGenreProfileSubGenreMetrics = async (
  dataSource: TDataSourceType,
  metric: TMetricType,
  filterParams: any,
) => {
  const metricOrderBy = generateSortMetricKey({ dataSource, metric }, SortType.Desc);
  const filterFetchParams = formatFilterFetchParams(filterParams);
  const params = {
    ...filterFetchParams,
    metrics: getMetrics(filterFetchParams?.granularity, dataSource, metric),
    group_by: ['sensortower_sub_genre'],
    order_by: [metricOrderBy],
    page: 1,
    page_size: MAX_ROWS_LIMIT,
    genre_keywords: [],
  };
  return fetchCasualGenreProfileMetrics('sub_genre', params);
};

export const fetchCasualGenreProfileSubGenreMetricsSeparateByDate = (
  dataSource: TDataSourceType,
  metric: TMetricType,
  filterParams: any,
) => {
  const metricOrderBy = generateSortMetricKey({ dataSource, metric }, SortType.Desc);
  const filterFetchParams = formatFilterFetchParams(filterParams);
  const params = {
    ...filterFetchParams,
    metrics: getMetrics(filterFetchParams?.granularity, dataSource, metric),
    group_by: ['date', 'sensortower_sub_genre'],
    order_by: [metricOrderBy],
    page: 1,
    page_size: MAX_ROWS_LIMIT,
    genre_keywords: [],

    top: {
      group_by: ['sensortower_sub_genre'],
      order_by: [metricOrderBy],
      size: 10,
    },
  };
  return fetchCasualGenreProfileMetrics('sub_genre_by_date', params);
};

export const fetchCasualGenreProfileSuperGenreMetrics = async (
  dataSource: TDataSourceType,
  metric: TMetricType,
  filterParams: any,
) => {
  const metricOrderBy = generateSortMetricKey({ dataSource, metric }, SortType.Desc);
  const filterFetchParams = formatFilterFetchParams(filterParams);
  const params = {
    ...filterFetchParams,
    metrics: getMetrics(filterFetchParams?.granularity, dataSource, metric),
    group_by: ['super_genre'],
    order_by: [metricOrderBy],
    page: 1,
    page_size: MAX_ROWS_LIMIT,
    genre_keywords: [],
  };
  return fetchCasualGenreProfileMetrics('super_genre', params);
};

export const fetchCasualGenreProfileSuperGenreMetricsSeparateByDate = (
  dataSource: TDataSourceType,
  metric: TMetricType,
  filterParams: any,
) => {
  const metricOrderBy = generateSortMetricKey({ dataSource, metric }, SortType.Desc);
  const filterFetchParams = formatFilterFetchParams(filterParams);
  const params = {
    ...filterFetchParams,
    metrics: getMetrics(filterFetchParams?.granularity, dataSource, metric),
    group_by: ['date', 'super_genre'],
    order_by: [metricOrderBy],
    page: 1,
    page_size: MAX_ROWS_LIMIT,
    genre_keywords: [],

    top: {
      group_by: ['super_genre'],
      order_by: [metricOrderBy],
      size: 10,
    },
  };
  return fetchCasualGenreProfileMetrics('super_genre_by_date', params);
};

export const fetchCasualGenreProfileMajorCategoryMetrics = async (
  dataSource: TDataSourceType,
  metric: TMetricType,
  filterParams: any,
) => {
  const metricOrderBy = generateSortMetricKey({ dataSource, metric }, SortType.Desc);
  const filterFetchParams = formatFilterFetchParams(filterParams);
  const params = {
    ...filterFetchParams,
    metrics: getMetrics(filterFetchParams?.granularity, dataSource, metric),
    group_by: ['major_category'],
    order_by: [metricOrderBy],
    page: 1,
    page_size: MAX_ROWS_LIMIT,
    genre_keywords: [],
  };
  return fetchCasualGenreProfileMetrics('major_category', params);
};

export const fetchCasualGenreProfileMajorCategoryMetricsSeparateByDate = (
  dataSource: TDataSourceType,
  metric: TMetricType,
  filterParams: any,
) => {
  const metricOrderBy = generateSortMetricKey({ dataSource, metric }, SortType.Desc);
  const filterFetchParams = formatFilterFetchParams(filterParams);
  const params = {
    ...filterFetchParams,
    metrics: getMetrics(filterFetchParams?.granularity, dataSource, metric),
    group_by: ['date', 'major_category'],
    order_by: [metricOrderBy],
    page: 1,
    page_size: MAX_ROWS_LIMIT,
    genre_keywords: [],

    top: {
      group_by: ['major_category'],
      order_by: [metricOrderBy],
      size: 10,
    },
  };
  return fetchCasualGenreProfileMetrics('major_category_by_date', params);
};

export const fetchCasualGenreProfileMidCategoryMetrics = async (
  dataSource: TDataSourceType,
  metric: TMetricType,
  filterParams: any,
) => {
  const metricOrderBy = generateSortMetricKey({ dataSource, metric }, SortType.Desc);
  const filterFetchParams = formatFilterFetchParams(filterParams);
  const params = {
    ...filterFetchParams,
    metrics: getMetrics(filterFetchParams?.granularity, dataSource, metric),
    group_by: ['mid_category'],
    order_by: [metricOrderBy],
    page: 1,
    page_size: MAX_ROWS_LIMIT,
    genre_keywords: [],
  };
  return fetchCasualGenreProfileMetrics('mid_category', params);
};

export const fetchCasualGenreProfileMidCategoryMetricsSeparateByDate = (
  dataSource: TDataSourceType,
  metric: TMetricType,
  filterParams: any,
) => {
  const metricOrderBy = generateSortMetricKey({ dataSource, metric }, SortType.Desc);
  const filterFetchParams = formatFilterFetchParams(filterParams);
  const params = {
    ...filterFetchParams,
    metrics: getMetrics(filterFetchParams?.granularity, dataSource, metric),
    group_by: ['date', 'mid_category'],
    order_by: [metricOrderBy],
    page: 1,
    page_size: MAX_ROWS_LIMIT,
    genre_keywords: [],

    top: {
      group_by: ['mid_category'],
      order_by: [metricOrderBy],
      size: 10,
    },
  };
  return fetchCasualGenreProfileMetrics('mid_category_by_date', params);
};

const USE_CUSTOM_FIELDS = ['entity_name', 'cover', 'publisher', 'publisher_country_abbre'];

const mergeGameDetails = (
  list?: ICasualGenreProfileMetricData[] | null,
  entityInfoMap?: IRecord | null,
): ICasualGenreProfileMetricData[] => {
  if (isEmpty(list)) {
    return [];
  }

  if (isEmpty(entityInfoMap)) {
    return list as ICasualGenreProfileMetricData[];
  }

  return list!.map((item) => {
    const gameId = item?.game_id || '';
    const itemEntityInfo = entityInfoMap?.[gameId];
    return {
      ...item,
      ...itemEntityInfo,
      name: itemEntityInfo?.entity_name || '',
      cover: itemEntityInfo?.cover || '',
      publisher: itemEntityInfo?.publisher || '',
      publisher_addr: itemEntityInfo?.publisher_country_abbre || '',
    };
  });
};

export const fetchCasualGenreProfileTopGameMetrics = async (
  dataSource: TDataSourceType,
  metric: TMetricType,
  filterParams: any,
) => {
  const metricOrderBy = generateSortMetricKey({ dataSource, metric: getGameMetric(metric) }, SortType.Desc);
  const filterFetchParams = formatFilterFetchParams(filterParams);
  let metrics = getMetrics(filterFetchParams?.granularity, dataSource, metric);
  metrics = metrics.map((item) => {
    return getGameDataSourceMetric(item);
  });
  const params = {
    ...filterFetchParams,
    metrics,
    group_by: ['game_id'],
    order_by: [metricOrderBy],
    page: 1,
    page_size: 10,
    genre_keywords: [],

    use_custom_fields: USE_CUSTOM_FIELDS,
  };
  const res = await fetchCasualGenreProfileMetrics('top_game', params);
  const resData = res?.data || {};
  const ext = res?.ext || {};
  const list = resData?.list || [];
  const entityInfoMap = ext?.entity_info || {};
  const listNew = mergeGameDetails(list, entityInfoMap);
  const resDataNew = { ...resData, list: listNew };

  return { ...res, data: resDataNew };
};

export const fetchCasualGenreProfileRisingGameMetrics = async (
  dataSource: TDataSourceType,
  metric: TMetricType,
  filterParams: any,
) => {
  const metricOrderBy = generateSortMetricKey(
    { dataSource, metric: getGameMetric(metric), ratio: true },
    SortType.Desc,
  );
  const metricFilters = getMetricFiltersOfMetricValueLimit(dataSource, metric, filterParams);
  const filterParamsNew = {
    ...filterParams,
    metric_filter: [...metricFilters],
  };
  const filterFetchParams = formatFilterFetchParams(filterParamsNew);
  let metrics = getMetrics(filterFetchParams?.granularity, dataSource, metric);
  metrics = metrics.map((item) => {
    return getGameDataSourceMetric(item);
  });
  const params = {
    ...filterFetchParams,
    metrics,
    group_by: ['game_id'],
    order_by: [metricOrderBy],
    page: 1,
    page_size: 10,
    genre_keywords: [],

    use_custom_fields: USE_CUSTOM_FIELDS,
  };
  const res = await fetchCasualGenreProfileMetrics('rising_game', params);
  const resData = res?.data || {};
  const ext = res?.ext || {};
  const list = resData?.list || [];
  const entityInfoMap = ext?.entity_info || {};
  const listNew = mergeGameDetails(list, entityInfoMap);
  const resDataNew = { ...resData, list: listNew };

  return { ...res, data: resDataNew };
};

export const fetchCasualGenreProfileGlobalPerformanceMetricsSeparatedByMarket = (
  dataSource: TDataSourceType,
  metric: TMetricType,
  filterParams: any,
) => {
  const metricOrderBy = generateSortMetricKey({ dataSource, metric }, SortType.Desc);
  const filterFetchParams = formatFilterFetchParams(filterParams);
  const params = {
    ...filterFetchParams,
    metrics: [...getMetrics(filterFetchParams?.granularity, dataSource, metric), 'game_count'],
    group_by: ['market'],
    order_by: [metricOrderBy],
    page: 1,
    page_size: MAX_ROWS_LIMIT,
    genre_keywords: [],
  };
  return fetchCasualGenreProfileMetrics('global_performance_by_market', params);
};

export const fetchCasualGenreProfileGlobalPerformanceMetricsSeparatedByDateAndMarket = (
  dataSource: TDataSourceType,
  metric: TMetricType,
  filterParams: any,
) => {
  const metricOrderBy = generateSortMetricKey({ dataSource, metric }, SortType.Desc);
  const filterFetchParams = formatFilterFetchParams(filterParams);
  const params = {
    ...filterFetchParams,
    metrics: getMetrics(filterFetchParams?.granularity, dataSource, metric),
    group_by: ['date', 'market'],
    order_by: [metricOrderBy],
    page: 1,
    page_size: MAX_ROWS_LIMIT,
    genre_keywords: [],

    top: {
      group_by: ['market'],
      order_by: [metricOrderBy],
      size: 6,
    },
  };
  return fetchCasualGenreProfileMetrics('global_performance_by_date_and_market', params);
};

const groupByMap: IRecord<string[]> = {
  sensortower__mainGenre: ['sensortower_genre'],
  sensortower__subGenre: ['sensortower_genre', 'sensortower_sub_genre'],
  appmagic__superGenre: ['super_genre'],
  appmagic__majorCategory: ['super_genre', 'major_category'],
  appmagic__midCategory: ['super_genre', 'major_category', 'mid_category'],
  appmagic__subCategory: ['super_genre', 'major_category', 'mid_category', 'sub_category'],
};

/**
 * 将排序状态转换为 order_by 参数
 * @param dataSource 数据源
 * @param metric 默认指标
 * @param sorts 排序状态列表
 * @returns order_by 参数数组
 */
const buildOrderBy = (
  dataSource: TDataSourceType,
  metric: TMetricType,
  sorts: IRatioMetricSortState<ICasualGenreProfileMetricData>[] | undefined,
): string[] => {
  // 如果有自定义排序，使用自定义排序
  if (sorts && sorts.length > 0) {
    return sorts.map((sort) => {
      const { dataSource, metric, mode, type } = sort;
      // 否则使用默认指标生成排序 key
      return generateSortMetricKey({ dataSource, metric, ratio: mode === SortMode.Ratio }, type);
    });
  }

  // 默认按当前指标降序排序
  const metricOrderBy = generateSortMetricKey({ dataSource, metric }, SortType.Desc);
  return [metricOrderBy];
};

const buildTopGameMetricsFilter = (
  columnKey: string,
  dataSource: TDataSourceType,
  filterParams: any,
): IMetricFilter[] => {
  const granularity = filterParams?.granularity || 'monthly';
  const startDate = filterParams?.start_date || '';
  const endDate = filterParams?.end_date || '';
  const startDateM = moment(startDate);
  const endDateM = moment(endDate);
  const dateRange: [moment.Moment, moment.Moment] = [startDateM, endDateM];

  return buildTopGamesMetricFilter(columnKey, dataSource, granularity, dateRange);
};

export const fetchCasualGenreProfileTableData = (params: IGenreTableModuleUpdateParams) => {
  const {
    genreSource,
    genreDimension,
    dataSource,
    metric,
    filterParams,
    sorts,
    pagingQuery,
    genreKeywords,
    cancelToken,
  } = params || {};
  const groupByKey = `${genreSource}__${genreDimension}`;
  const groupBy = groupByMap[groupByKey] || groupByMap.sensortower__mainGenre;
  const filterFetchParams = formatFilterFetchParams(filterParams);
  const granularity = filterFetchParams?.granularity || 'monthly';

  // 构建排序参数
  const orderBy = buildOrderBy(dataSource, metric, sorts);

  // 构建分页参数
  const page = pagingQuery?.pageIndex || 1;
  const pageSize = pagingQuery?.pageSize || 50;

  const fetchParams = {
    ...filterFetchParams,
    metrics: getDatasourceMetrics(filterFetchParams.granularity),
    group_by: groupBy,
    order_by: orderBy,
    page,
    page_size: pageSize,
    genre_keywords: genreKeywords || [],
    top_games_metric_filter: {
      top_by_download: [],
      top_by_avg_dau: [],
      top_by_avg_mau: [],
      rising_by_download: buildTopGameMetricsFilter('risingGamesByDownloads', dataSource, filterFetchParams),
      rising_by_avg_dau:
        granularity === 'monthly' ? [] : buildTopGameMetricsFilter('risingGamesByDAU', dataSource, filterFetchParams),
      rising_by_avg_mau:
        granularity === 'daily' ? [] : buildTopGameMetricsFilter('risingGamesByMAU', dataSource, filterFetchParams),
    },
    top_games_size: 4,
    use_custom_fields: ['entity_name', 'cover'],
  };
  return fetchCasualGenreProfileMetrics('table_data', fetchParams, undefined, cancelToken);
};
